import { formatWallTime, NO_AVOID, parseWallTime, type Avoid, type ParsedLink, type Stop } from "@viamissa/gmaps-link";
import type { LatLng } from "@viamissa/geo";
import { useState } from "react";
import { KM_PER_MILE } from "../lib/format";
import { DEFAULT_SETTINGS, type MassChoice, type TimeChoice } from "../lib/trip";

export type Draft = {
  stops: Stop[];
  via: LatLng[];
  avoid: Avoid;
  timeKind: TimeChoice["kind"];
  /** `YYYY-MM-DDTHH:MM`, local to the origin (depart) or destination (arrive). */
  timeText: string;
  masses: MassChoice;
  corridorMi: number;
  maxDetourMin: number;
  marginMin: number;
  limitHours: boolean;
  startHour: number;
  endHour: number;
};

function nextHalfHour(): string {
  const now = new Date();
  now.setMinutes(now.getMinutes() < 30 ? 30 : 60, 0, 0);
  return formatWallTime({
    year: now.getFullYear(),
    month: now.getMonth() + 1,
    day: now.getDate(),
    hour: now.getHours(),
    minute: now.getMinutes(),
  });
}

export function draftFromLink(link: ParsedLink | null): Draft {
  const time = link?.time ?? { kind: "now" as const };
  return {
    stops: link?.stops ?? [{ label: "" }, { label: "" }],
    via: link?.via ?? [],
    avoid: link?.avoid ?? NO_AVOID,
    timeKind: time.kind,
    timeText: time.kind === "now" ? nextHalfHour() : formatWallTime(time.wall),
    masses: DEFAULT_SETTINGS.masses,
    corridorMi: Math.round(DEFAULT_SETTINGS.corridorKm / KM_PER_MILE),
    maxDetourMin: DEFAULT_SETTINGS.maxDetourMin,
    marginMin: DEFAULT_SETTINGS.marginMin,
    limitHours: false,
    startHour: 8,
    endHour: 20,
  };
}

export function timeChoice(draft: Draft): TimeChoice | null {
  if (draft.timeKind === "now") return { kind: "now" };
  const wall = parseWallTime(draft.timeText);
  return wall ? { kind: draft.timeKind, wall } : null;
}

type Props = {
  draft: Draft;
  onChange: (draft: Draft) => void;
  onSubmit: (mode: "itinerary" | "churches") => void;
  busy: boolean;
};

export function TripEditor({ draft, onChange, onSubmit, busy }: Props) {
  const [advanced, setAdvanced] = useState(false);
  const set = <K extends keyof Draft>(key: K, value: Draft[K]) => onChange({ ...draft, [key]: value });
  const setStop = (index: number, label: string) =>
    set(
      "stops",
      draft.stops.map((stop, i) => (i === index ? { label } : stop)),
    );
  const ready = draft.stops.every((stop) => stop.currentLocation || stop.label.trim()) && draft.stops.length >= 2;

  return (
    <form
      className="editor"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit("itinerary");
      }}
    >
      <fieldset>
        <legend>Route</legend>
        {draft.stops.map((stop, index) => (
          <div className="stop" key={index}>
            <span className="badge">{index === 0 ? "A" : index === draft.stops.length - 1 ? "B" : index}</span>
            <input
              aria-label={index === 0 ? "Start" : index === draft.stops.length - 1 ? "Destination" : `Stop ${index}`}
              placeholder={index === 0 ? "Start (or leave as your location)" : index === draft.stops.length - 1 ? "Destination" : "Stop"}
              value={stop.currentLocation ? "Your location" : stop.label}
              onChange={(event) => setStop(index, event.target.value)}
            />
            {draft.stops.length > 2 && (
              <button
                type="button"
                className="icon"
                aria-label="Remove stop"
                onClick={() => set("stops", draft.stops.filter((_, i) => i !== index))}
              >
                ×
              </button>
            )}
          </div>
        ))}
        <button
          type="button"
          className="link"
          onClick={() => set("stops", [...draft.stops.slice(0, -1), { label: "" }, draft.stops[draft.stops.length - 1]!])}
        >
          Add a stop
        </button>
        {draft.via.length > 0 && (
          <p className="fine">Keeps the {draft.via.length === 1 ? "detour" : `${draft.via.length} detours`} you dragged in Google Maps.</p>
        )}
      </fieldset>

      <fieldset>
        <legend>When</legend>
        <div className="row">
          <select value={draft.timeKind} onChange={(event) => set("timeKind", event.target.value as Draft["timeKind"])}>
            <option value="now">Leave now</option>
            <option value="depart">Depart at</option>
            <option value="arrive">Arrive by</option>
          </select>
          {draft.timeKind !== "now" && (
            <input
              type="datetime-local"
              aria-label={draft.timeKind === "depart" ? "Departure time" : "Arrival time"}
              value={draft.timeText}
              onChange={(event) => set("timeText", event.target.value)}
            />
          )}
        </div>
        <p className="fine">
          {draft.timeKind === "arrive" ? "Local time at the destination. " : draft.timeKind === "depart" ? "Local time at the start. " : ""}
          Uses each church's own Mass times for the day you pass it. Arrive 15 minutes early; 1 hour for Sunday and vigil
          Masses, 30 minutes on weekdays.
        </p>
      </fieldset>

      <fieldset>
        <legend>Avoid</legend>
        <div className="row checks">
          {(["tolls", "highways", "ferries"] as (keyof Avoid)[]).map((key) => (
            <label key={key}>
              <input type="checkbox" checked={draft.avoid[key]} onChange={(event) => set("avoid", { ...draft.avoid, [key]: event.target.checked })} />
              {key[0]!.toUpperCase() + key.slice(1)}
            </label>
          ))}
        </div>
      </fieldset>

      <button type="button" className="link" onClick={() => setAdvanced(!advanced)} aria-expanded={advanced}>
        {advanced ? "Fewer options" : "More options"}
      </button>
      {advanced && (
        <fieldset className="advanced">
          <label>
            <input
              type="checkbox"
              checked={draft.masses === "daily"}
              onChange={(event) => set("masses", event.target.checked ? "daily" : "obligation")}
            />
            On trips of more than a day, fit in Mass every day, not just Sundays and holy days
          </label>
          <label>
            Search within
            <input type="number" min={1} max={30} value={draft.corridorMi} onChange={(event) => set("corridorMi", Number(event.target.value))} /> mi of the route
          </label>
          <label>
            Longest detour
            <input type="number" min={5} max={90} step={5} value={draft.maxDetourMin} onChange={(event) => set("maxDetourMin", Number(event.target.value))} /> min each way
          </label>
          <label>
            Extra time per drive
            <input type="number" min={0} max={60} step={5} value={draft.marginMin} onChange={(event) => set("marginMin", Number(event.target.value))} /> min
          </label>
          <label>
            <input type="checkbox" checked={draft.limitHours} onChange={(event) => set("limitHours", event.target.checked)} />
            Only drive between
            <input type="number" min={0} max={23} value={draft.startHour} disabled={!draft.limitHours} onChange={(event) => set("startHour", Number(event.target.value))} />
            and
            <input type="number" min={1} max={24} value={draft.endHour} disabled={!draft.limitHours} onChange={(event) => set("endHour", Number(event.target.value))} />
            o'clock
          </label>
        </fieldset>
      )}

      <div className="submit">
        <button type="submit" disabled={busy || !ready}>
          Plan Mass on the way
        </button>
        <button type="button" className="ghost" disabled={busy || !ready} onClick={() => onSubmit("churches")}>
          Just list churches
        </button>
      </div>
    </form>
  );
}
